import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Empty, Pagination, Space, Spin, Tag, Typography } from 'antd';
import ReactMarkdown from 'react-markdown';
import dayjs from 'dayjs';
import axios from 'axios';
import { ENDPOINTS } from '../../helpers/api/apiConfig';

const { Text } = Typography;

interface CommonSectionProps {
    section: any;
    previewAreaId: number;
    measurementId?: number | null;
}

type AnalysisSummary = {
    id: number;
    analysisType?: string;
    summary?: string;
    recommendations?: any;
    detectedIssues?: any;
    qualityScore?: number | null;
    status?: string;
    analysisVersion?: number;
    createdAt?: string;
    updatedAt?: string;
    periodStart?: string;
    periodEnd?: string;
};

const PAGE_SIZE = 6;

export const ReportLatestAnalysis: React.FC<CommonSectionProps> = ({
    section,
    previewAreaId,
    measurementId,
}) => {
    const [loading, setLoading] = useState(false);
    const [analysisList, setAnalysisList] = useState<AnalysisSummary[]>([]);
    const [selectedAnalysisId, setSelectedAnalysisId] = useState<number | null>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [error, setError] = useState<string | null>(null);

    const scope = useMemo(() => {
        if (measurementId) return 'measurement';
        if (previewAreaId) return 'area';
        return null;
    }, [measurementId, previewAreaId]);

    useEffect(() => {
        if (!scope) {
            setAnalysisList([]);
            setSelectedAnalysisId(null);
            setCurrentPage(1);
            return;
        }
        let cancelled = false;
        setLoading(true);
        setError(null);

        const fetchAnalysis = async () => {
            try {
                const url = scope === 'measurement'
                    ? ENDPOINTS.MEASUREMENT_ANALYSES(measurementId as number)
                    : ENDPOINTS.AREA_ANALYSES(previewAreaId);
                const res = await axios.get(url, { withCredentials: true });
                if (cancelled) return;
                const payload = Array.isArray(res.data?.data) ? res.data.data : [];
                setAnalysisList(payload);
                setCurrentPage(1);
                setSelectedAnalysisId((prev) => {
                    if (prev && payload.some((entry: AnalysisSummary) => entry.id === prev)) {
                        return prev;
                    }
                    return payload[0]?.id ?? null;
                });
            } catch (err) {
                if (cancelled) return;
                console.error('[ReportLatestAnalysis] Failed to load analysis', err);
                setError('Failed to load analysis');
                setAnalysisList([]);
                setSelectedAnalysisId(null);
                setCurrentPage(1);
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };

        fetchAnalysis();

        return () => {
            cancelled = true;
        };
    }, [scope, measurementId, previewAreaId]);

    const selectedAnalysis = useMemo(() => {
        if (analysisList.length === 0) {
            return null;
        }
        if (selectedAnalysisId) {
            return analysisList.find((entry) => entry.id === selectedAnalysisId) || analysisList[0];
        }
        return analysisList[0];
    }, [analysisList, selectedAnalysisId]);

    const pagedAnalyses = useMemo(() => {
        const start = (currentPage - 1) * PAGE_SIZE;
        return analysisList.slice(start, start + PAGE_SIZE);
    }, [analysisList, currentPage]);

    const analysisScopeLabel = useMemo(() => {
        if (measurementId) return `Measurement ${measurementId}`;
        if (previewAreaId) return `Area ${previewAreaId}`;
        return null;
    }, [measurementId, previewAreaId]);

    if (!scope) {
        return <Alert message="Select an area or measurement to view analysis." type="info" showIcon />;
    }

    if (loading) {
        return (
            <div style={{ textAlign: 'center', padding: 24 }}>
                <Spin tip="Loading analysis..." />
            </div>
        );
    }

    if (error) {
        return <Alert message={error} type="error" showIcon />;
    }

    if (!selectedAnalysis || !selectedAnalysis.summary) {
        return <Empty description="No analysis found for this selection" image={Empty.PRESENTED_IMAGE_SIMPLE} />;
    }

    const createdAtLabel = selectedAnalysis.createdAt
        ? dayjs(selectedAnalysis.createdAt).format('YYYY-MM-DD HH:mm')
        : null;
    const periodLabel = selectedAnalysis.periodStart && selectedAnalysis.periodEnd
        ? `${dayjs(selectedAnalysis.periodStart).format('YYYY-MM-DD')} → ${dayjs(selectedAnalysis.periodEnd).format('YYYY-MM-DD')}`
        : null;

    const latestId = analysisList[0]?.id ?? null;
    const selectedId = selectedAnalysisId ?? latestId;

    return (
        <div className="report-analysis-workspace">
            <div className="report-analysis-head">
                <div className="report-analysis-title">{section?.title || 'Latest analysis'}</div>
                <div className="report-analysis-meta">
                    {[analysisScopeLabel, createdAtLabel].filter(Boolean).join(' · ')}
                </div>
            </div>

            <div className={`report-analysis-body ${analysisList.length > 1 ? '' : 'report-analysis-body--single'}`}>
                {analysisList.length > 1 ? (
                    <aside className="report-analysis-rail">
                        <div className="report-analysis-section-head">
                            <div className="report-analysis-section-title">Analysis runs</div>
                            <div className="report-analysis-section-subtitle">
                                {analysisList.length} runs available
                            </div>
                        </div>

                        <div className="report-analysis-run-list">
                            {pagedAnalyses.map((entry) => {
                                const createdLabel = entry.createdAt
                                    ? dayjs(entry.createdAt).format('YYYY-MM-DD HH:mm')
                                    : `Analysis #${entry.id}`;
                                const isLatest = latestId !== null && entry.id === latestId;
                                const period = entry.periodStart && entry.periodEnd
                                    ? `${dayjs(entry.periodStart).format('YYYY-MM-DD')} → ${dayjs(entry.periodEnd).format('YYYY-MM-DD')}`
                                    : null;
                                const isSelected = selectedId !== null && entry.id === selectedId;

                                return (
                                    <div
                                        key={entry.id}
                                        className={`report-analysis-run ${isSelected ? 'report-analysis-run--selected' : ''}`}
                                        onClick={() => setSelectedAnalysisId(entry.id)}
                                        role="button"
                                        tabIndex={0}
                                        onKeyDown={(event) => {
                                            if (event.key === 'Enter' || event.key === ' ') {
                                                event.preventDefault();
                                                setSelectedAnalysisId(entry.id);
                                            }
                                        }}
                                    >
                                        <div className="report-analysis-run-top">
                                            <Text strong>{createdLabel}</Text>
                                            {isLatest ? <span className="report-analysis-run-chip">Latest</span> : null}
                                        </div>
                                        <div className="report-analysis-run-tags">
                                            {entry.analysisVersion !== null && entry.analysisVersion !== undefined ? (
                                                <Tag color="geekblue">v{entry.analysisVersion}</Tag>
                                            ) : null}
                                            {entry.analysisType ? <Tag color="purple">{entry.analysisType}</Tag> : null}
                                            {entry.status ? (
                                                <Tag color={entry.status === 'completed' ? 'blue' : 'orange'}>
                                                    {entry.status}
                                                </Tag>
                                            ) : null}
                                        </div>
                                        {period ? (
                                            <div className="report-analysis-run-period">{period}</div>
                                        ) : null}
                                    </div>
                                );
                            })}
                        </div>

                        {analysisList.length > PAGE_SIZE ? (
                            <Pagination
                                size="small"
                                current={currentPage}
                                pageSize={PAGE_SIZE}
                                total={analysisList.length}
                                onChange={setCurrentPage}
                                showSizeChanger={false}
                            />
                        ) : null}
                    </aside>
                ) : null}

                <div className="report-analysis-main">
                    <div className="report-analysis-summary">
                        <div className="report-analysis-summary-head">
                            <div>
                                <div className="report-analysis-summary-title">Summary</div>
                                {periodLabel ? (
                                    <div className="report-analysis-summary-subtitle">{periodLabel}</div>
                                ) : null}
                            </div>
                            <div className="report-analysis-tags">
                                {selectedAnalysis.analysisType && (
                                    <Tag color="purple">{selectedAnalysis.analysisType}</Tag>
                                )}
                                {selectedAnalysis.analysisVersion !== null && selectedAnalysis.analysisVersion !== undefined && (
                                    <Tag color="geekblue">Version {selectedAnalysis.analysisVersion}</Tag>
                                )}
                                {selectedAnalysis.qualityScore !== null && selectedAnalysis.qualityScore !== undefined && (
                                    <Tag color="green">Quality {selectedAnalysis.qualityScore}/10</Tag>
                                )}
                                {selectedAnalysis.status && (
                                    <Tag color={selectedAnalysis.status === 'completed' ? 'blue' : 'orange'}>
                                        {selectedAnalysis.status}
                                    </Tag>
                                )}
                            </div>
                        </div>

                        <div className="report-analysis-markdown markdown-content">
                            <ReactMarkdown>{selectedAnalysis.summary}</ReactMarkdown>
                        </div>

                        <div className="report-analysis-footer">
                            {periodLabel ? <span className="report-analysis-footer-item">Period: {periodLabel}</span> : null}
                            {createdAtLabel ? <span className="report-analysis-footer-item">Updated: {createdAtLabel}</span> : null}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ReportLatestAnalysis;
